import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Save, FilePlus2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { PageHead, StatusBadge, OLIVE, errMsg, fmtDate } from "../components/dpa/dpaUtils";
import { OrgForm } from "../components/dpa/OrganisationsTab";

export default function DpaOrganisationPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [o, setO] = useState(null);
  const load = () => api.get(`/dpa/organisations/${id}`).then(({ data }) => setO(data));
  useEffect(() => {
    let live = true;
    api.get(`/dpa/organisations/${id}`).then(({ data }) => live && setO(data));
    return () => { live = false; };
  }, [id]);
  if (!o) return null;
  const save = async () => {
    const body = Object.fromEntries(["name", "legal_name", "trading_name", "registered_address", "company_number", "contact_name", "contact_email", "contact_phone", "dp_contact_email", "notes"].map((k) => [k, o[k] || ""]));
    try { await api.put(`/dpa/organisations/${id}`, body); toast.success("Organisation saved", { description: "Issued agreements keep the details they were issued with." }); load(); }
    catch (e) { toast.error("Save failed", { description: errMsg(e) }); }
  };
  const generate = async () => {
    try { const { data } = await api.post(`/dpa/organisations/${id}/agreements`, {}); toast.success(`Draft ${data.reference} created`); nav(`/compliance/agreements/${data.id}`); }
    catch (e) { toast.error("Could not generate DPA", { description: errMsg(e) }); }
  };
  const current = o.agreements.find((a) => a.status !== "superseded");
  return (
    <div className="p-8 space-y-6 fade-up" data-testid="organisation-page">
      <Link to="/compliance" className="text-xs inline-flex items-center gap-1 text-muted-foreground"><ArrowLeft className="h-3 w-3" />All organisations</Link>
      <PageHead kicker="Organisation · Data Processing Agreement" title={o.name}>
        {current ? <StatusBadge status={current.status} label={current.status_label} testId="org-current-status" /> : <StatusBadge status="not_created" label="Not Created" testId="org-current-status" />}
      </PageHead>
      <Card className="rounded-none p-6 space-y-4" data-testid="org-agreement-panel">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Agreements</div>
          <Button className="rounded-none text-white" style={OLIVE} onClick={generate} data-testid="generate-dpa-btn"><FilePlus2 className="h-4 w-4 mr-1" />{current ? "Generate replacement DPA" : "Generate DPA"}</Button>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
            <th className="py-2">Reference</th><th>Version</th><th>Status</th><th>Issued</th><th>Effective</th><th>Review</th></tr></thead>
          <tbody>
            {o.agreements.map((a) => (
              <tr key={a.id} className="border-t cursor-pointer hover:bg-slate-50" style={{ borderColor: "var(--hi-border)" }} onClick={() => nav(`/compliance/agreements/${a.id}`)} data-testid={`org-agreement-${a.reference}`}>
                <td className="py-2 font-mono text-xs">{a.reference}</td><td>v{a.template_version}</td>
                <td><StatusBadge status={a.status} label={a.status_label} /></td>
                <td>{fmtDate(a.issue_date)}</td><td>{fmtDate(a.effective_date)}</td><td>{fmtDate(a.next_review_date)}</td>
              </tr>
            ))}
            {!o.agreements.length && <tr><td colSpan={6} className="py-4 text-muted-foreground text-center">No agreement yet.</td></tr>}
          </tbody>
        </table>
      </Card>
      <Card className="rounded-none p-6 space-y-4" data-testid="org-details-card">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Client legal entity details</div>
        <OrgForm value={o} onChange={setO} />
        <Button className="rounded-none text-white" style={OLIVE} onClick={save} data-testid="save-org-btn"><Save className="h-4 w-4 mr-1" />Save details</Button>
      </Card>
    </div>
  );
}
